import { useEffect, useState } from "react";
import { Mail, MapPin, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { getStorefrontSettings, sendContactMessage, type StorefrontSettings } from "@/lib/api";
import TikTokIcon from "@/components/TikTokIcon";

const emptyStorefrontSettings: StorefrontSettings = {
  contact_email: "",
  contact_phone: "",
  studio_address: "",
  tiktok_url: "",
  checkout_note: "",
};

export default function Contact() {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [storefrontSettings, setStorefrontSettings] = useState<StorefrontSettings>(emptyStorefrontSettings);
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const hasContactDetails = Boolean(storefrontSettings.contact_email || storefrontSettings.contact_phone || storefrontSettings.studio_address || storefrontSettings.tiktok_url);

  useEffect(() => {
    getStorefrontSettings().then(setStorefrontSettings).catch(() => undefined);
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    try {
      await sendContactMessage(form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-shell">
      <div className="container">
        <section className="page-hero">
          <span className="eyebrow">{t.nav.contact}</span>
          <h1 className="sr-only">{t.contact.title}</h1>
          <p className="lede">{t.contact.subtitle}</p>
        </section>

        <div className={`grid gap-6 ${hasContactDetails ? "lg:grid-cols-[0.8fr_1.2fr]" : "max-w-3xl"}`}>
          {hasContactDetails && <aside className="panel p-7">
            <div className="space-y-6">
              {storefrontSettings.contact_email && <div className="flex items-start gap-3">
                <Mail size={18} style={{ color: "var(--color-terracotta)", marginTop: 3 }} />
                <div>
                  <p className="mb-1 text-xs font-bold uppercase tracking-[0.16em]" style={{ color: "var(--color-text-light)" }}>
                    Email
                  </p>
                  <a href={`mailto:${storefrontSettings.contact_email}`}>{storefrontSettings.contact_email}</a>
                </div>
              </div>}
              {storefrontSettings.contact_phone && <div className="flex items-start gap-3">
                <Phone size={18} style={{ color: "var(--color-terracotta)", marginTop: 3 }} />
                <div>
                  <p className="mb-1 text-xs font-bold uppercase tracking-[0.16em]" style={{ color: "var(--color-text-light)" }}>
                    {t.checkout.phone}
                  </p>
                  <a href={`tel:${storefrontSettings.contact_phone.replace(/[^+\d]/g, "")}`}>{storefrontSettings.contact_phone}</a>
                </div>
              </div>}
              {storefrontSettings.studio_address && <div className="flex items-start gap-3">
                <MapPin size={18} style={{ color: "var(--color-terracotta)", marginTop: 3 }} />
                <div>
                  <p className="mb-1 text-xs font-bold uppercase tracking-[0.16em]" style={{ color: "var(--color-text-light)" }}>
                    Studio
                  </p>
                  <p className="whitespace-pre-line">{storefrontSettings.studio_address}</p>
                </div>
              </div>}
              {storefrontSettings.tiktok_url && <a href={storefrontSettings.tiktok_url} target="_blank" rel="noopener noreferrer" className="btn-secondary inline-flex w-fit items-center gap-2">
                <TikTokIcon />{t.contact.tiktok}
              </a>}
            </div>
          </aside>}

          <form onSubmit={handleSubmit} className="panel form-grid p-7 md:p-8">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="form-label">{t.contact.name} *</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                  className="input-shell"
                />
              </div>
              <div>
                <label className="form-label">{t.contact.email} *</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(event) => setForm({ ...form, email: event.target.value })}
                  className="input-shell"
                />
              </div>
            </div>
            <div>
              <label className="form-label">{t.contact.subject}</label>
              <input
                type="text"
                value={form.subject}
                onChange={(event) => setForm({ ...form, subject: event.target.value })}
                className="input-shell"
              />
            </div>
            <div>
              <label className="form-label">{t.contact.message} *</label>
              <textarea
                required
                rows={7}
                value={form.message}
                onChange={(event) => setForm({ ...form, message: event.target.value })}
                className="input-shell resize-none"
              />
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full sm:w-fit">
              {loading ? t.common.loading : t.contact.send}
              <Send size={16} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
